import { ref, computed } from 'vue';
import {
  CatalogueSortField,
  getFilterDefinition,
  type CatalogueFilterOp,
  type CatalogueFilterValue,
  type CatalogueSortOrder,
  type CatalogueQueryParams,
  type CatalogueFilterClause,
  type CatalogueFilterDefinition,
} from '@/models/CatalogueFilters';
import type { PaginatedResponse } from '@/models/Catalogue';

export interface UseCatalogueFiltersOptions {
  defaultItemsPerPage?: number;
}

/**
 * Composable for managing catalogue filter state and pagination
 */
export const useCatalogueFilters = (
  options: UseCatalogueFiltersOptions = {},
) => {
  const { defaultItemsPerPage = 48 } = options;

  // Filter state as a reactive Map (string keys to support both enum and dynamic cf.* keys)
  const filters = ref(new Map<string, CatalogueFilterValue>());

  // Track which optional filters are currently active (visible in the bar)
  const activeFilterKeys = ref(new Set<string>());

  // Per-filter operator overrides (when user changes from the default)
  const operatorOverrides = ref(new Map<string, CatalogueFilterOp>());

  // Extra filter definitions (e.g. dynamic custom field filters)
  const extraFilterDefinitions = ref<CatalogueFilterDefinition[]>([]);

  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(defaultItemsPerPage);
  const totalPages = ref(0);
  const hasNext = ref(false);
  const hasPrevious = ref(false);
  const isLoading = ref(false);

  // Sorting state
  const sortBy = ref<CatalogueSortField | null>(null);
  const sortOrder = ref<CatalogueSortOrder>('asc');

  /**
   * Reset pagination to first page
   */
  const resetToFirstPage = () => {
    currentPage.value = 1;
  };

  /**
   * Resolve filter definition from static registry or extra definitions
   */
  const resolveFilterDefinition = (
    key: string,
  ): CatalogueFilterDefinition | undefined => {
    return (
      getFilterDefinition(key) ??
      extraFilterDefinitions.value.find((f) => f.key === key)
    );
  };

  /**
   * Set a filter value
   */
  const setFilter = (key: string, value: CatalogueFilterValue) => {
    if (value === null || value === '') {
      filters.value.delete(key);
    } else {
      filters.value.set(key, value);
    }
    resetToFirstPage();
  };

  /**
   * Get a filter value
   */
  const getFilter = (key: string): CatalogueFilterValue => {
    return filters.value.get(key) ?? null;
  };

  /**
   * Set operator override for a filter
   */
  const setOperator = (key: string, op: CatalogueFilterOp) => {
    operatorOverrides.value.set(key, op);
    // If filter has a value, re-trigger a query by resetting page
    if (filters.value.has(key)) {
      resetToFirstPage();
    }
  };

  /**
   * Get the effective operator for a filter (override or default)
   */
  const getOperator = (key: string): CatalogueFilterOp | null => {
    const override = operatorOverrides.value.get(key);
    if (override) {
      return override;
    }
    const def = resolveFilterDefinition(key);
    return def?.defaultOp ?? null;
  };

  /**
   * Add an optional filter to the active set
   */
  const addFilter = (key: string) => {
    activeFilterKeys.value.add(key);
  };

  /**
   * Remove a filter from active set and clear its value and operator
   */
  const removeFilter = (key: string) => {
    activeFilterKeys.value.delete(key);
    filters.value.delete(key);
    operatorOverrides.value.delete(key);
    resetToFirstPage();
  };

  /**
   * Check if a filter is active (visible in bar)
   */
  const isFilterActive = (key: string): boolean => {
    return activeFilterKeys.value.has(key);
  };

  /**
   * Check if a filter has a value set
   */
  const hasFilterValue = (key: string): boolean => {
    const value = filters.value.get(key);
    return value !== null && value !== undefined && value !== '';
  };

  /**
   * Set sorting field and/or order, resets to first page
   */
  const setSorting = (
    field?: CatalogueSortField | null,
    order?: CatalogueSortOrder,
  ) => {
    if (field !== undefined) {
      sortBy.value = field;
    }
    if (order !== undefined) {
      sortOrder.value = order;
    }
    resetToFirstPage();
  };

  /**
   * Register extra filter definitions (e.g. from custom fields endpoint)
   */
  const setExtraFilterDefinitions = (defs: CatalogueFilterDefinition[]) => {
    extraFilterDefinitions.value = defs;
  };

  /**
   * Build query parameters for the API call.
   * Converts internal filter state to JSON filter/sort clauses.
   */
  const getParams = (): CatalogueQueryParams => {
    const params: CatalogueQueryParams = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    const clauses: CatalogueFilterClause[] = [];
    filters.value.forEach((value, key) => {
      if (value === null || value === '') {
        return;
      }
      const def = resolveFilterDefinition(key);
      if (!def) {
        return;
      }
      const op = operatorOverrides.value.get(key) ?? def.defaultOp;
      clauses.push({
        field: def.field,
        op,
        value: value as string | number | boolean,
      });
    });
    if (clauses.length > 0) {
      params.filters = clauses;
    }

    if (sortBy.value) {
      params.sort = [{ field: sortBy.value, order: sortOrder.value }];
    }

    return params;
  };

  /**
   * Update pagination state from API response
   */
  const updatePaginationState = (response: PaginatedResponse<unknown>) => {
    totalPages.value = response.pagination.totalPages;
    hasNext.value = response.pagination.hasNext;
    hasPrevious.value = response.pagination.hasPrevious;
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page: number) => {
    currentPage.value = page;
  };

  /**
   * Clear all filters and reset
   */
  const clearAllFilters = () => {
    filters.value.clear();
    activeFilterKeys.value.clear();
    operatorOverrides.value.clear();
    sortBy.value = null;
    sortOrder.value = 'asc';
    resetToFirstPage();
  };

  /**
   * Check if any filters have values
   */
  const hasActiveFilters = computed(() => {
    return filters.value.size > 0;
  });

  /**
   * Set loading state
   */
  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  return {
    // Filter state
    filters,
    activeFilterKeys,
    operatorOverrides,
    extraFilterDefinitions,

    // Sorting state
    sortBy,
    sortOrder,

    // Pagination state
    currentPage,
    itemsPerPage,
    totalPages,
    hasNext,
    hasPrevious,
    isLoading,

    // Computed
    hasActiveFilters,

    // Filter methods
    setFilter,
    getFilter,
    setOperator,
    getOperator,
    addFilter,
    removeFilter,
    isFilterActive,
    hasFilterValue,
    clearAllFilters,
    setExtraFilterDefinitions,
    resolveFilterDefinition,

    // Sorting methods
    setSorting,

    // Pagination methods
    getParams,
    updatePaginationState,
    handlePageChange,
    setLoading,
    resetToFirstPage,
  };
};
